/**
 * The validated-range check (revision directive RB-2, decision D-2,
 * finalized 2026-10-17 — DECISION_LOG Addendum 18).
 *
 * The validation studies (docs/E2_E4_E1B_REPORT.md;
 * docs/E3_FLAGSHIP_REPORT.md) measured the shipped pipeline over a
 * bounded envelope: per-area samples from 6 respondents up (E-3) and
 * the mid-20s up (E-2), prevalence generated from 0.02, and
 * acceptable conditional performance up to the 0.40 prevalence band.
 * A fitted cell outside the D-2 finalized triggers is flagged
 * OUT_OF_VALIDATED_RANGE, because there the studies either did not
 * measure the disclosed bias or measured materially degraded
 * performance:
 *
 * - Median respondents per fitted area below 6 — the smallest
 *   per-area samples any study drew (E-3 Section 1: replicate
 *   samples draw 6 to 30 respondents per area by construction;
 *   per-area respondent means run 7.6 to 25.6). The provisional
 *   trigger of 30 was dropped: E-3 validated the 6-to-30 regime
 *   under the shipped (benchmarked) configuration, and its size
 *   bands show no bias gradient (Section 4: -0.01497 at n < 20 vs
 *   -0.01478 at 20 <= n < 50, unbenchmarked), so a trigger at 30
 *   flagged the validated envelope itself.
 * - Any model estimate below 0.02 — the floor of the E-3
 *   generating span (Sections 1 and 7: the design targets
 *   prevalence 0.02 to 0.30). The provisional 0.04 was dropped:
 *   flagship cells live in [0.02, 0.10) in bulk (1,479 of 2,660
 *   overall areas at true p < 0.10) and pass D-1 benchmarked
 *   there, while the nearest studied band below the envelope
 *   (E-2 Section 1.2, p < 0.10) already shows the model losing to
 *   the direct estimate on realized RMSE on both designs.
 * - Any model estimate at or above 0.40 — the band where both
 *   studies measure collapse: E-3 Section 4 under the shipped
 *   benchmarked configuration records conditional bias -0.0866
 *   with coverage 0.516 -> 0.636, and E-2 Section 1.2 records
 *   bias -0.037 (logit) / -0.033 (arcsine) with coverage
 *   0.898 / 0.909 on 234 / 236 areas. The 0.20-0.40 band does not
 *   ground a trigger: it is E-2's healthiest band (bias -0.0009 /
 *   -0.0034, coverage 0.956 / 0.948).
 *
 * Two further triggers arrived with the re-review verdict
 * (RB-4, 2026-10-17), which found that runs outside the validated
 * configuration were neither flagged nor accurately described:
 *
 * - Benchmarking not applied, for any reason — the validated
 *   configuration is the benchmarked one (decision D-4): at
 *   flagship scale the unbenchmarked arms measure aggregate bias
 *   -0.0148 overall (z = -62.6) and -0.0266 for subgroups
 *   (z = -106.8), with overall coverage 0.885 (Quick) / 0.852
 *   (Full). Unbenchmarked runs arise both when a user turns
 *   benchmarking off and when it is skipped automatically
 *   because population counts are incomplete (BENCHMARK_SKIPPED),
 *   so the assessment input carries the run's benchmarking
 *   status ('applied' / 'user-off' / 'skipped') from the layer
 *   that knows it, and the trigger fires for the latter two.
 * - Fewer than 10 fitted areas — cells with 5 to 9 areas fit by
 *   default since directive C-7, but no study covered fewer
 *   than 30 areas and E-3 kept a 10-area minimum as a
 *   study-design choice, so those cells are unstudied.
 *
 * A fifth trigger arrived with Re-Review 4 (Section 3 of the
 * verdict; Decision Log Addenda 49 Part B and 50 Item 3):
 *
 * - No frame PSU counts supplied, on a microdata run whose
 *   median respondents per fitted area fall below the E-2 study
 *   scale. The corrected (P2) variance basis applies only where
 *   the design supplies first-stage sampling fractions; without
 *   counts a run uses the with-replacement basis, whose measured
 *   record (study RB-6, shipped Quick default) passes at the
 *   E-2 scale (coverage 92.6% logit-truth / 93.2% arcsine-truth,
 *   inside the 92-97% band) and fails at flagship scale (88.8%
 *   pooled; 83.3% in the under-20-respondents band). The trigger
 *   threshold is fixed from the recorded E-2 designs themselves:
 *   across both truth designs under the RB-6 replicate streams
 *   (40 populations x 200 replicates each), the per-replicate
 *   median respondents per fitted area runs 54 to 72 (pooled
 *   median 72), so the smallest cell size at which the no-counts
 *   basis was measured to pass is 54; flagship (E-3) cells by
 *   contrast measure 18 to 24 (pooled median 21). A no-counts
 *   cell whose median falls below 54 is therefore smaller than
 *   every cell in the record that validates its basis. The
 *   assessment input carries the run's frame-counts status from
 *   the layer that knows it, exactly as for benchmarking status;
 *   area-level runs never reach this assessment (their input
 *   publishes direct estimates and carries no microdata design
 *   that could supply counts).
 *
 * The status default is fail-safe (Re-Review 5, Section 1 Item 3
 * note; DECISION_LOG Addendum 70): a caller that does not report
 * the status is assessed as 'not-supplied', so a microdata path
 * that fails to report it cannot silently skip the flag. The
 * one caller the trigger does not apply to — the area-level
 * pipeline, which carries no microdata design — states its
 * status explicitly rather than inheriting any default.
 *
 * The flag is informational: it never suppresses, alters, or
 * blocks an estimate.
 */
import { DEFAULT_VALIDATED_RANGE } from './config.js';
/** Resolve a partial or absent configuration against the D-2 defaults. */
export function resolveValidatedRangeConfig(config) {
    return {
        minMedianRespondents: config?.minMedianRespondents ?? DEFAULT_VALIDATED_RANGE.minMedianRespondents,
        minModelEstimate: config?.minModelEstimate ?? DEFAULT_VALIDATED_RANGE.minModelEstimate,
        maxModelEstimate: config?.maxModelEstimate ?? DEFAULT_VALIDATED_RANGE.maxModelEstimate,
        minFittedAreas: config?.minFittedAreas ?? DEFAULT_VALIDATED_RANGE.minFittedAreas,
        minMedianRespondentsNoCounts: config?.minMedianRespondentsNoCounts ??
            DEFAULT_VALIDATED_RANGE.minMedianRespondentsNoCounts,
    };
}
/** Median of a sample; NaN for an empty sample, the mean of the two middle values for an even count. */
export function medianOf(values) {
    if (values.length === 0)
        return Number.NaN;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
}
/**
 * Assess one fitted cell against the validated-range triggers.
 * `respondentCounts` holds the unweighted respondent count of every
 * fitted (in-sample) area — its length is the cell's fitted area
 * count; `modelEstimates` holds the cell's final model estimates
 * on the proportion scale (after benchmarking, when applied);
 * `benchmarkingStatus` is the run's benchmarking outcome for the
 * cell, supplied by the calling pipeline (re-review RB-4; it
 * defaults to 'applied' so a caller that does not track the
 * outcome assesses the measurement triggers only).
 * `frameCountsStatus` is the run's frame-PSU-counts status,
 * supplied the same way (Re-Review 4 Section 3; it defaults to
 * the fail-safe 'not-supplied', so a caller that does not report
 * it is assessed as a no-counts run and cannot silently skip
 * the no-counts trigger — Re-Review 5, Section 1 Item 3 note).
 * The two floor triggers are exclusive (a
 * median or an estimate of exactly the trigger does not flag);
 * the ceiling trigger is inclusive (an estimate of exactly the
 * trigger flags), matching the evidence bands, which are
 * defined as p >= 0.40. The area-count trigger is exclusive (a
 * cell fitting exactly the trigger count does not flag), and
 * the no-counts trigger is exclusive in the same way (a median
 * of exactly the E-2 floor does not flag).
 */
export function assessValidatedRange(respondentCounts, modelEstimates, config, benchmarkingStatus = 'applied', frameCountsStatus = 'not-supplied') {
    const resolved = resolveValidatedRangeConfig(config);
    const medianRespondentsPerArea = medianOf(respondentCounts);
    const smallestModelEstimate = modelEstimates.length > 0 ? Math.min(...modelEstimates) : undefined;
    const largestModelEstimate = modelEstimates.length > 0 ? Math.max(...modelEstimates) : undefined;
    const medianTrigger = Number.isFinite(medianRespondentsPerArea) &&
        medianRespondentsPerArea < resolved.minMedianRespondents;
    const estimateTrigger = smallestModelEstimate !== undefined &&
        smallestModelEstimate < resolved.minModelEstimate;
    const estimateHighTrigger = largestModelEstimate !== undefined &&
        largestModelEstimate >= resolved.maxModelEstimate;
    const benchmarkingTrigger = benchmarkingStatus !== 'applied';
    const fewAreasTrigger = respondentCounts.length < resolved.minFittedAreas;
    const noFrameCountsTrigger = frameCountsStatus === 'not-supplied' &&
        Number.isFinite(medianRespondentsPerArea) &&
        medianRespondentsPerArea < resolved.minMedianRespondentsNoCounts;
    return {
        flagged: medianTrigger ||
            estimateTrigger ||
            estimateHighTrigger ||
            benchmarkingTrigger ||
            fewAreasTrigger ||
            noFrameCountsTrigger,
        medianRespondentsPerArea,
        ...(smallestModelEstimate !== undefined ? { smallestModelEstimate } : {}),
        ...(largestModelEstimate !== undefined ? { largestModelEstimate } : {}),
        fittedAreaCount: respondentCounts.length,
        benchmarkingStatus,
        frameCountsStatus,
        triggers: {
            medianRespondents: medianTrigger,
            modelEstimate: estimateTrigger,
            modelEstimateHigh: estimateHighTrigger,
            benchmarkingNotApplied: benchmarkingTrigger,
            fewAreas: fewAreasTrigger,
            noFrameCounts: noFrameCountsTrigger,
        },
    };
}
/** The OUT_OF_VALIDATED_RANGE issue for a flagged cell (Appendix A style). */
export function outOfValidatedRangeIssue(cellId, assessment, config) {
    const resolved = resolveValidatedRangeConfig(config);
    const parts = [];
    if (assessment.triggers.medianRespondents) {
        parts.push(`the median fitted area has ${assessment.medianRespondentsPerArea} respondents, ` +
            `below the trigger of ${resolved.minMedianRespondents}`);
    }
    // The estimate triggers name the threshold only, never the extreme
    // value itself: the extreme can be a suppressed domain's withheld
    // estimate, and this message is rendered verbatim in the app's Run
    // Report and the methods report (the RB-1 count-recovery scan
    // caught exactly that leak for the ceiling trigger on the standard
    // fixture, Addendum 18). The measured extremes stay on the cell
    // result (validatedRange), which is not a published surface. The
    // median is safe to quote: it is a cell aggregate, not a domain
    // value, and respondent medians were the RB-2 accepted surface.
    if (assessment.triggers.modelEstimate) {
        parts.push(`a model estimate in the cell falls below ` +
            `the trigger of ${resolved.minModelEstimate}`);
    }
    if (assessment.triggers.modelEstimateHigh) {
        parts.push(`a model estimate in the cell reaches ` +
            `the high-prevalence trigger of ${resolved.maxModelEstimate}`);
    }
    // The RB-4 parts state run-level facts only — the benchmarking
    // outcome and the fitted area count against its trigger — the
    // same no-domain-values discipline as the estimate triggers
    // above: nothing here can be a withheld domain value.
    if (assessment.triggers.benchmarkingNotApplied) {
        parts.push(assessment.benchmarkingStatus === 'user-off'
            ? 'benchmarking was turned off for this run, and the validated configuration is the benchmarked one'
            : 'benchmarking was not applied in this run, and the validated configuration is the benchmarked one');
    }
    if (assessment.triggers.fewAreas) {
        parts.push(`the cell fits ${assessment.fittedAreaCount} areas, ` +
            `below the trigger of ${resolved.minFittedAreas}`);
    }
    // The no-counts part states the same class of run-level facts:
    // the run supplied no frame counts (so its variance basis is
    // the with-replacement one), and the cell's median respondents
    // per area against the E-2 floor its basis was measured at.
    if (assessment.triggers.noFrameCounts) {
        parts.push(`the run supplied no frame PSU counts, so its sampling ` +
            `variances use the with-replacement basis, and the median ` +
            `fitted area has ${assessment.medianRespondentsPerArea} respondents, ` +
            `below the E-2 study scale of ${resolved.minMedianRespondentsNoCounts} ` +
            `at which that basis was measured`);
    }
    // The closing sentence names no single mechanism: the RB-4
    // triggers (benchmarking status, fitted area count) are not
    // transform-bias cases, so it states the disjunction that is true
    // of every trigger — the studies either did not measure the
    // pipeline in this configuration or measured it materially
    // degraded there.
    return {
        code: 'OUT_OF_VALIDATED_RANGE',
        severity: 'warning',
        cellId,
        message: 'This cell is outside the validated range: ' +
            parts.join('; ') +
            '. In this range the validation studies either did not measure ' +
            'the performance of the pipeline or measured materially ' +
            'degraded performance, so treat these estimates with added ' +
            'care (revision directive RB-2, decision D-2).',
    };
}
//# sourceMappingURL=validated-range.js.map